import { ExportOutlined, LinkOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import type { KeyboardEvent } from 'react';
import type { PreviewInfo } from '@shared/api';
import { ScrollContainer } from '@shared/ui/scroll-container';
import { useTranslation } from 'react-i18next';
import styles from '../preview.module.scss';

interface PreviewCardProps {
  preview: PreviewInfo;
  onEdit: (preview: PreviewInfo) => void;
}

export const PreviewCard = ({ preview, onEdit }: PreviewCardProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'preview' });
  const externalUrl = /^https?:\/\//i.test(preview.url) ? preview.url : `https://${preview.url}`;

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    onEdit(preview);
  };

  return (
    <article
      className={styles.card}
      role="button"
      tabIndex={0}
      aria-label={t('cardLabel', { url: preview.url })}
      onClick={() => onEdit(preview)}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.cardHeader}>
        <span className={styles.cardLinkIcon} aria-hidden>
          <LinkOutlined />
        </span>
        <a
          className={styles.cardUrl}
          href={externalUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={t('openUrl', { url: preview.url })}
          title={preview.url}
          onClick={(event) => event.stopPropagation()}
        >
          <span>{preview.url}</span>
          <ExportOutlined aria-hidden />
        </a>
      </div>

      <div className={styles.cardContent}>
        <Typography.Text type="secondary" className={styles.cardLabel}>
          {t('messageLabel')}
        </Typography.Text>
        <p className={styles.cardQuestion} title={preview.question}>
          {preview.question}
        </p>
      </div>

      <div className={styles.answersRow}>
        <Typography.Text type="secondary" className={styles.cardLabel}>
          {t('answersLabel')}
        </Typography.Text>
        <ScrollContainer
          width="auto"
          height="auto"
          marginRight="0"
          style={{ flex: '1 1 0', minWidth: 0 }}
        >
          <div className={styles.chips} aria-label={t('edit.answers')}>
            {preview.answers.map((answer, index) => (
              <span className={styles.chip} title={answer} key={`${answer}-${index}`}>
                {answer}
              </span>
            ))}
          </div>
        </ScrollContainer>
      </div>
    </article>
  );
};
